import {
  Box,
  Disclaimer,
  lightTheme,
  P,
  TruncatedText,
} from '@vgrubs/components';
import { ReactNode } from 'react';
import styled from 'styled-components';

/**
 * One person, in a list of them: a colleague to add, a merchant to open a
 * thread with, a member already in the group.
 *
 * All three lists are the same row - name, one line of context, something on
 * the right - and they were three copies of it before. The `Box` and its
 * spacing come from `AddLocationAgentModal`, so a picker in chat looks like a
 * picker anywhere else in the admin.
 */

/** A flex child needs this to ellipse - it defaults to `min-width: auto`. */
const Grow = styled.div`
  flex: 1;
  min-width: 0;
`;

/**
 * The hover underline goes on the SPAN, not on the text block around it.
 *
 * `P`'s own `$hoverUnderline` looks like it should do this and silently does
 * nothing here: the name is wrapped in `TruncatedText`, which renders an
 * `inline-block` so it can ellipse, and CSS does not propagate text-decoration
 * into an atomic inline box. The rule has to name the span itself.
 */
const NameLine = styled.div<{ $clickable?: boolean }>`
  min-width: 0;
  /*
   * Shrink-wraps the name so the hover and the click stop where the text does.
   * As a block it filled the row, and the underline lit up from anywhere along
   * a line that is mostly empty space - which reads as the whole row being the
   * link when only the name is. The max-width keeps TruncatedText able to
   * ellipse: an inline-block is shrink-to-fit, so it still gives way to the
   * container rather than running past it.
   */
  display: inline-block;
  max-width: 100%;

  ${({ $clickable }) =>
    $clickable &&
    `
    cursor: pointer;

    :hover span {
      text-decoration: underline;
    }
  `}
`;

type Props = {
  name: string;
  /**
   * Role and phone - whatever tells this person from the next one. Short by
   * design: it has to survive the row's width whole, since two people with
   * the same name differ here and nowhere else.
   */
  subtitle?: ReactNode;
  /**
   * A second quiet line for the long tail - the locations someone works at -
   * which may ellipse. On its own line so it can never push the subtitle out:
   * one line holding both cut the phone number off for anyone with a few
   * locations.
   */
  detail?: ReactNode;
  /** Set when the row itself is the control (picking, toggling, opening). */
  onClick?: () => void;
  /**
   * Set when the NAME is its own link, as in a member list where the row is
   * not clickable but the person has a details page.
   */
  onNameClick?: () => void;
  /** Red while a group draft has this person selected. */
  selected?: boolean;
  /** Radio, chip or delete button on the right. */
  trailing?: ReactNode;
};

export const ConversationPersonRow = ({
  name,
  subtitle,
  detail,
  onClick,
  onNameClick,
  selected,
  trailing,
}: Props) => {
  const color = selected ? lightTheme.primary : lightTheme.darkGray;

  return (
    <Box
      $mb="8"
      $flex
      $alignCenter
      $between
      $gap="16px"
      $pointer={!!onClick}
      onClick={onClick}
    >
      <Grow>
        <NameLine
          $clickable={!!onNameClick}
          onClick={
            onNameClick
              ? (event: React.MouseEvent) => {
                  // The row is not itself a link; stop the click reaching it.
                  event.stopPropagation();
                  onNameClick();
                }
              : undefined
          }
        >
          <P $m="0" $bold $left>
            <TruncatedText color={color}>{name}</TruncatedText>
          </P>
        </NameLine>

        {!!subtitle && (
          <Disclaimer $m="0" $left>
            <TruncatedText color={lightTheme.lightGray}>
              {subtitle}
            </TruncatedText>
          </Disclaimer>
        )}

        {!!detail && (
          <Disclaimer $m="0" $left>
            <TruncatedText color={lightTheme.lightGray}>{detail}</TruncatedText>
          </Disclaimer>
        )}
      </Grow>

      {trailing}
    </Box>
  );
};
